import React, { useEffect, useState, useRef } from "react";
import { useDisclosure, useToast } from "@chakra-ui/react";
import {
  Wallet as WalletIcon,
  Coins,
  Trophy,
  Lock,
  ArrowDownToLine,
  History,
  CreditCard,
  PencilLine,
  Info,
  ShieldCheck,
  ChevronRight,
  Plus,
  QuoteIcon,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import RechargeModal from "../../../components/Wallet/RechargeModal";
import TransactionHistory from "../../../components/Wallet/TransactionHistory";
import RechargeHistory from "../../../components/Wallet/RechargeHistory";
import {
  getWalletBalance,
  unifiedWithdraw,
} from "../../../services/walletService";
import { useUser } from "../../../hooks/use-user";
import axios from "../../../axios";
import { Link, useNavigate } from "react-router-dom";
import { FaAngleLeft } from "react-icons/fa";
import Coin from "../../../Images/adhyayi_coin.png";
import { Query } from "@tanstack/react-query";
import WithdrawModal from "./WithdrawModal";
import WalletInfoModal from "./WalletInfoModal";

const Wallet = () => {
  const { data: user, refetch } = useUser();
  const {
    isOpen: isRechargeOpen,
    onOpen: onRechargeOpen,
    onClose: onRechargeClose,
  } = useDisclosure();
  const {
    isOpen: isInfoOpen,
    onOpen: onInfoOpen,
    onClose: onInfoClose,
  } = useDisclosure();
  const toast = useToast();

  const [walletData, setWalletData] = useState({
    totalBalance: 0,
    availableBalance: 0,
    lockedBalance: 0,
    rechargedBalance: 0,
    contestBalance: 0,
    lifetimeEarned: 0,
    lifetimeContestEarned: 0,
    lifetimeRecharged: 0,
    lifetimeReferralEarned: 0,
    referralBalance: 0,
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  // Unified Withdraw State
  const [withAmount, setWithAmount] = useState("");
  const [isFormVisible, setFormVisible] = useState(false);
  const intial = {
    aadharNo: "",
    ifsc: "",
    bankName: "",
    accountNo: "",
    holderName: "",
    mobile: "",
    email: "",
    upiId: "",
  };
  const [formData, setFormData] = useState(intial);
  const [hasExistingKyc, setHasExistingKyc] = useState(false);
  const firstInputRef = useRef(null);
  const [isWithdrawOpen, setWithdrawOpen] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
const [popupType, setPopupType] = useState(""); // kyc | insufficient

  // Fetch existing KYC data
  const fetchKycData = async () => {
    try {
      const response = await axios.get("getKyc");
      if (response.data?.result) {
        const kycData = response.data.result;
        setFormData({
          aadharNo: kycData.aadharNo || "",
          ifsc: kycData.ifsc || "",
          bankName: kycData.bankName || "",
          accountNo: kycData.accountNo || "",
          holderName: kycData.holderName || "",
          mobile: kycData.mobile || "",
          email: kycData.email || "",
          upiId: kycData.upiId || "",
        });
        setHasExistingKyc(true);
      }
    } catch (error) {
      console.log("No existing KYC found");
      setHasExistingKyc(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await getWalletBalance();
      setWalletData(data.result);
      console.log(data.result,"wallet data");
      if (user) {
        refetch();
        setFormData((prev) => ({
          ...prev,
          email: user.email || prev.email,
          mobile: user.mobile || prev.mobile,
        }));
      }
    } catch (error) {
      console.error("Error fetching wallet data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchKycData();
  }, []);

  const navigate = useNavigate();

const handlePrevPage = () => {
  navigate(-1); // goes back to previous page
};

const openTransactionHistoryPage = () => {
  navigate("/dash/transaction-history");
};

  // --- Unified Withdraw Logic ---
  const sendWithdrawRequest = async (credits) => {
    // amount.preventDefault();
      const amount = Number(credits) / 100;
    setWithAmount(amount);

 if (!user?.kycDone) {
  // KYC Pending Popup
  setPopupType("kyc");
  setShowPopup(true);
  return;
}

if (walletData.availableBalance < Number(credits)) {
  // Insufficient Balance Popup
  setPopupType("insufficient");
  setShowPopup(true);
  return;
}

    try {
      await unifiedWithdraw(Number(amount));
      toast({
        title: "Withdraw request sent successfully.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      setWithAmount("");
      fetchData();
    } catch (error) {
      console.log("Withdrawal Error:", error);
      let message = "Withdrawal failed";

      if (error.response) {
        message =
          error.response.data?.message ||
          error.response.data?.error ||
          JSON.stringify(error.response.data);
      } else if (error.request) {
        message =
          "No response from server. Please check your internet connection.";
      } else {
        message = error.message;
      }

      toast({
        title: "Withdrawal Failed",
        description: message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const sendKyc = async (e) => {
    e.preventDefault();
    try {
      if (hasExistingKyc) {
        await axios.put("updateKyc", formData);
      } else {
        await axios.post("addKyc", formData);
      }
      toast({
        title: hasExistingKyc
          ? "KYC updated successfully."
          : "KYC details sent successfully.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      setHasExistingKyc(true);
      refetch();
    } catch (err) {
      toast({
        title: "Something went wrong.",
        description: err?.response?.data?.message || err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

useEffect(() => {
  const handleEsc = (e) => {
    if (e.key === "Escape") {
      setShowPopup(false);
    }
  };

  window.addEventListener("keydown", handleEsc);

  return () => {
    window.removeEventListener("keydown", handleEsc);
  };
}, []);

  const tabs = [
    { label: "Withdraw", icon: ArrowDownToLine },
    { label: "Transactions", icon: History },
    { label: "Recharge History", icon: CreditCard },
  ];

 const estimateValue =  walletData.totalBalance / 100 ;
  const statCards = [
    {
      label: "Total Balance",
      value: walletData.totalBalance,
      icon: WalletIcon,
      color: "from-purple-500 to-indigo-500",
      borderColor: "border-purple-500/30",
      helpText: "All Credits",
    },
    {
      label: "Recharged (Play Only)",
      value: walletData.rechargedBalance,
      icon: Coins,
      color: "from-green-500 to-emerald-500",
      borderColor: "border-green-500/30",
      helpText: "Purchased Credits",
    },
    {
      label: "Contest & Referral",
      value:
        (walletData.contestBalance || 0) + (walletData.referralBalance || 0),
      icon: Trophy,
      color: "from-orange-500 to-amber-500",
      borderColor: "border-orange-500/30",
      helpText: "Withdrawable Anytime",
    },
    {
      label: "Quiz Earnings",
      value: walletData.lockedBalance,
      icon: Lock,
      color: "from-gray-400 to-gray-500",
      borderColor: "border-gray-500/30",
      helpText: walletData.isLockReleased ? "Unlocked" : "Releases soon",
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#2A1B3D] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400 text-sm">Loading wallet...</p>
        </div>
      </div>
    );
  }

return (
  <div className="relative h-screen overflow-hidden bg-white text-white lg:bg-[#F7F3FA]">
    {/* Background Elements */}
    <div className="absolute top-0 left-0 w-full h-[500px] bg-gradient-to-b from-purple-900/20 to-transparent pointer-events-none"></div>
    <div className="absolute top-[-100px] right-[-100px] w-72 h-72 bg-purple-600/10 rounded-full blur-3xl pointer-events-none"></div>
    <div className="absolute bottom-0 left-[-100px] w-64 h-64 bg-indigo-600/10 rounded-full blur-3xl pointer-events-none"></div>

    {/* MOBILE VIEW */}
    <div className="relative z-10 flex h-screen flex-col lg:hidden">
      {/* Fixed My Credits Header */}
      <div className="shrink-0 bg-[#34163F] rounded-b-3xl p-5 text-white">
        <div className="flex justify-between items-center mb-5">
          {/* <button
            onClick={handlePrevPage}
            className="bg-white/20 w-9 h-9 flex items-center justify-center rounded-full"
          >
            <FaAngleLeft />
          </button> */}

          <h2 className="font-semibold text-center text-lg">My Credits</h2>

          <div
            onClick={onInfoOpen}
            className="bg-white/20 w-9 h-9 flex items-center justify-center rounded-full cursor-pointer"
          >
            <Info size={18} />
          </div>
        </div>

        <div className="text-center">
          <p className="text-sm opacity-80">Available Credits</p>

          <div className="flex justify-center items-center gap-2 mt-2">
            <h1 className="text-5xl font-bold">
              {walletData.totalBalance}
            </h1>
            <img src={Coin} alt="Coin Icon" className="w-24 h-16" />
          </div>

          <p className="text-xs opacity-70 mt-1">
            Estimated Valueee ₹{estimateValue.toFixed(0)}
          </p>
        </div>

        <div className="flex gap-3 mt-5">
          <button
            onClick={onRechargeOpen}
            className="flex-1 flex items-center justify-center gap-2 bg-[#F5A623] text-white py-3 rounded-full font-semibold shadow-md"
          >
            <span className="text-xl">＋</span>
            Get Credits
          </button>

          <button
            onClick={() => setWithdrawOpen(true)}
            className="flex-1 flex items-center justify-center gap-2 bg-white text-[#4B2D83] py-3 rounded-full font-semibold shadow-md"
          >
            <span className="text-sm text-yellow-400">↗</span>
            Request Withdraw
          </button>
        </div>
      </div>

      {/* Scroll starts below My Credits */}
      <div className="flex-1 overflow-y-auto px-4 pb-8">
        {/* Reward Cards */}
        <div className="grid grid-cols-3 gap-3 mt-6">
          <div className="bg-white rounded-xl p-3 text-center shadow-md flex flex-col items-center">
            <div className="w-10 h-10 flex items-center justify-center bg-[#4B2D83] rounded-full mb-2">
              <QuoteIcon className="text-white" size={18} />
            </div>

            <p className="text-xs text-gray-500">Quiz Reward</p>
            <p className="font-bold text-black">{walletData.lockedBalance}</p>
            <span className="text-xs text-white rounded-xl p-2 bg-[#F5A623]">
              Credits
            </span>
          </div>

          <div className="bg-white rounded-xl p-3 text-center shadow-md flex flex-col items-center">
            <div className="w-10 h-10 flex items-center justify-center mb-2 bg-[#4B2D83] rounded-full">
              <Trophy size={18} className="text-white" />
            </div>

            <p className="text-xs text-gray-500">Contest Reward</p>
            <p className="font-bold text-black">{walletData.contestBalance}</p>
            <span className="text-xs text-white rounded-xl p-2 bg-[#F5A623]">
              Credits
            </span>
          </div>

          <div className="bg-white rounded-xl p-3 text-center shadow-md flex flex-col items-center">
            <div className="w-10 h-10 flex items-center justify-center bg-[#4B2D83] rounded-full mb-2">
              <WalletIcon className="text-white" size={18} />
            </div>

            <p className="text-xs text-gray-500">Get Credits</p>
            <p className="font-bold text-black">
              {walletData.rechargedBalance}
            </p>
            <span className="text-xs text-white rounded-xl p-2 bg-[#F5A623]">
              Credits
            </span>
          </div>
        </div>

        {/* Mobile Transition History Preview */}
        <div className="mt-5 w-full">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-black">
              Transition History
            </h3>

            <button
              onClick={openTransactionHistoryPage}
              className="text-purple-600 text-sm"
            >
              View All
            </button>
          </div>
          
          <TransactionHistory
  limit={6}
  hidePagination
  showHeading={false}
  showFilter={false}
/>
        </div>
      </div>
    </div>

    {/* DESKTOP VIEW */}
    <div className="relative z-10 hidden h-screen overflow-hidden px-8 py-8 lg:block">
      <div className="mx-auto grid h-full max-w-[1180px] grid-cols-[420px_1fr] gap-8">
        {/* Left Desktop */}
        <div className="overflow-hidden mt-10">
          <div className="bg-[#34163F] rounded-[28px] p-5 text-white shadow-xl">
            <div className="flex justify-between items-center mb-5">
              {/* <button
                onClick={handlePrevPage}
                className="bg-white/20 w-9 h-9 flex items-center justify-center rounded-full"
              >
                <FaAngleLeft />
              </button> */}

              <h2 className="font-semibold text-lg">My Credits</h2>

              <div
                onClick={onInfoOpen}
                className="bg-white/20 w-9 h-9 flex items-center justify-center rounded-full cursor-pointer"
              >
                <Info size={18} />
              </div>
            </div>

            <div className="text-center">
              <p className="text-sm opacity-80">Available Credits</p>

              <div className="flex justify-center items-center gap-2 mt-2">
                <h1 className="text-5xl font-bold">
                  {walletData.totalBalance}
                </h1>
                <img src={Coin} alt="Coin Icon" className="w-24 h-16" />
              </div>

              <p className="text-xs opacity-70 mt-1">
                Estimated Value ₹{estimateValue.toFixed(0)}
              </p>
            </div>

            <div className="flex gap-3 mt-5">
              <button
                onClick={onRechargeOpen}
                className="flex-1 flex items-center justify-center gap-2 bg-[#F5A623] text-white py-3 rounded-full font-semibold shadow-md"
              >
                <span className="text-xl">＋</span>
                Get Credits
              </button>

              <button
                onClick={() => setWithdrawOpen(true)}
                className="flex-1 flex items-center justify-center gap-2 bg-white text-[#4B2D83] py-3 rounded-full font-semibold shadow-md"
              >
                <span className="text-sm text-yellow-400">↗</span>
                Request Withdraw
              </button>
            </div>
          </div>

          {/* Desktop Reward Cards */}
          <div className="grid grid-cols-1 gap-4 mt-6">
            <div className="bg-white rounded-xl p-4 text-center shadow-md flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-[#4B2D83] rounded-full">
                <QuoteIcon className="text-white" size={18} />
              </div>

              <div className="flex-1 text-left">
                <p className="text-xs text-gray-500">Quiz Reward</p>
                <p className="font-bold text-black">{walletData.lockedBalance}</p>
              </div>

              <span className="text-xs text-white rounded-xl p-2 bg-[#F5A623]">
                Credits
              </span>
            </div>

            <div className="bg-white rounded-xl p-4 text-center shadow-md flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-[#4B2D83] rounded-full">
                <Trophy size={18} className="text-white" />
              </div>

              <div className="flex-1 text-left">
                <p className="text-xs text-gray-500">Contest Reward</p>
                <p className="font-bold text-black">{walletData.contestBalance}</p>
              </div>

              <span className="text-xs text-white rounded-xl p-2 bg-[#F5A623]">
                Credits
              </span>
            </div>

            <div className="bg-white rounded-xl p-4 text-center shadow-md flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-[#4B2D83] rounded-full">
                <WalletIcon className="text-white" size={18} />
              </div>

              <div className="flex-1 text-left">
                <p className="text-xs text-gray-500">Get Credits</p>
                <p className="font-bold text-black">
                  {walletData.rechargedBalance}
                </p>
              </div>

              <span className="text-xs text-white rounded-xl p-2 bg-[#F5A623]">
                Credits
              </span>
            </div>
          </div>
        </div>

        {/* Right Desktop - no page scroll */}
        <div className="overflow-hidden mt-10">
          <div className="h-full bg-white rounded-[28px] shadow-xl p-6 text-black overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-2xl font-bold text-[#34163F]">
                  Transition History
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  All wallet rewards, withdraws and credits activity
                </p>
              </div>

              <button
                onClick={openTransactionHistoryPage}
                className="text-sm font-semibold text-[#562566]"
              >
                View All
              </button>
            </div>
            <div className="overflow-hidden">
              <TransactionHistory
  limit={6}
  hidePagination
  showHeading={false}
  showFilter={false}
/>
            </div>
          </div>
           
        </div>
      </div>
    </div>

    <RechargeModal
      isOpen={isRechargeOpen}
      onClose={onRechargeClose}
      onSuccess={fetchData}
      user={user}
      walletBalance={walletData.totalBalance}
    />

    <WithdrawModal
      isOpen={isWithdrawOpen}
      onClose={() => setWithdrawOpen(false)}
      walletData={walletData}
      onWithdraw={(credits) => {
        // setWithAmount(amt);
        sendWithdrawRequest(credits);
      }}
    />

    <WalletInfoModal isOpen={isInfoOpen} onClose={onInfoClose} />
    {showPopup && (
  <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
  onClick={() => setShowPopup(false)}
  >
    <div className="w-full max-w-sm rounded-[32px] border-2 border-yellow-300 bg-white p-6 text-center shadow-2xl"
     onClick={(e) => e.stopPropagation()}
     >

      <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-yellow-100">
        <div className="flex h-12 w-12 items-center justify-center rounded-full border-4 border-yellow-300 text-yellow-300 text-2xl font-bold">
          !
        </div>
      </div>

      {popupType === "kyc" ? (
        <>
          <h2 className="text-3xl font-bold text-yellow-300 mb-3">
            KYC Required
          </h2>

          <p className="text-gray-600 mb-5">
            Your KYC verification is pending.
            Complete KYC to enable withdrawals.
          </p>
         <Link to="/dash/kyc-status">
          <button
            onClick={() => {
              setShowPopup(false);
              setFormVisible(true);
            }}
            className="w-full rounded-full bg-yellow-300 py-3 font-bold text-white"
          >
            Complete KYC
          </button>
          </Link>
        </>
      ) : (
        <>
          <h2 className="text-3xl font-bold text-red-500 mb-3">
            Withdrawal Failed
          </h2>

          <p className="text-gray-600 mb-4">
            Insufficient Balance Available
          </p>

          <div className="space-y-1 text-gray-700 mb-5">
            <p>
              Requested : ₹{withAmount} ({withAmount * 100} Credits)
            </p>

            <p>
              Contest : {walletData.contestBalance} Credits
            </p>

            <p>
              Referral : {walletData.referralBalance} Credits
            </p>

            <p>
              Quiz : {walletData.lockedBalance} Credits
            </p>
          </div>

          <button
            onClick={() => setShowPopup(false)}
            className="w-full rounded-full bg-red-500 py-3 font-bold text-white"
          >
            OK
          </button>
        </>
      )}
    </div>
  </div>
)}
  </div>
  
);
};

export default Wallet;
